import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Box,
  IconButton,
  CssBaseline
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { ThemeProvider } from "@mui/material/styles";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { themeDevelopment, themeProduction } from "./theme";
import AgentsTab from "./tabs/AgentsTab";
import ToolsTab from "./tabs/ToolsTab";
import AssignTab from "./tabs/AssignTab";
import Login from "./components/Login";
import { EnvironmentProvider, useEnvironment } from "./context/EnvironmentContext";

// Restaurar TabPanel
function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AppContent() {
  const [tabValue, setTabValue] = useState(0);
  const [agents, setAgents] = useState([]);
  const [tools, setTools] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isDevelopment } = useEnvironment();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && token === process.env.REACT_APP_TOKEN) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (token) => {
    setIsAuthenticated(true);
    localStorage.setItem('token', token);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
  };

  // Restaurar handler de cambio de pestaña
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  // --- Carga de datos (Agents, Tools) --- (Restaurar la versión original si hubo cambios)
  const fetchAgents = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config`);
      if (!res.ok) return;
      const data = await res.json();
      setAgents(data || []); // Mantener el aseguramiento de array
    } catch (error) {
      console.error("Error al obtener agentes:", error);
      setAgents([]);
    }
  };

  const fetchTools = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/tool`);
      if (!res.ok) return;
      const data = await res.json();
      setTools(data || []); // Mantener el aseguramiento de array
    } catch (error) {
      console.error("Error al obtener tools:", error);
      setTools([]);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchTools();
  }, []);

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={isDevelopment ? themeDevelopment : themeProduction}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: (theme) => theme.palette.primary.main 
        }}>
          <Login onLogin={handleLogin} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={isDevelopment ? themeDevelopment : themeProduction}>
      <CssBaseline />
      <DndProvider backend={HTML5Backend}>
        <AppBar
          position="sticky"
          sx={{ borderRadius: '0 0 8px 8px', boxShadow: "0 2px 6px rgba(0,0,0,0.1)" }}
        >
          <Toolbar>
            <IconButton size="large" edge="start" color="inherit" aria-label="menu" sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Mi Aplicación de Agentes {isDevelopment ? 'Development' : 'Production'}
            </Typography>
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              textColor="inherit"
              TabIndicatorProps={{ style: { height: 4, borderRadius: 2 } }}
            >
              <Tab label="Agents" />
              <Tab label="Tools" />
              <Tab label="Assign Tools" />
            </Tabs>
            <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 2 }}>
              <Typography variant="body2">Logout</Typography>
            </IconButton>
          </Toolbar>
        </AppBar>

        <TabPanel value={tabValue} index={0}>
          <AgentsTab agents={agents} fetchAgents={fetchAgents} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <ToolsTab tools={tools} fetchTools={fetchTools} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <AssignTab agents={agents} tools={tools} />
        </TabPanel>
      </DndProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <EnvironmentProvider>
      <AppContent />
    </EnvironmentProvider>
  );
}

export default App;
